import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Heart, MessageSquare, Send, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { timeAgo } from "@/pages/Community";

const TOPIC_COLORS = {
  General: "#00FFC2", Gaming: "#B980FF", Music: "#FF0055", IRL: "#7CF5FF",
  Creators: "#FFD600", Support: "#00FFC2", Feedback: "#FFD600",
};

export default function CommunityPost() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const load = () => {
    api.get(`/community/posts/${id}`)
      .then(({ data }) => { setPost(data.post); setComments(data.comments); })
      .catch(() => setNotFound(true));
  };
  useEffect(load, [id]); // eslint-disable-line

  const vote = async () => {
    if (!user) { toast("Sign in to react"); return; }
    try {
      const { data } = await api.post(`/community/posts/${id}/vote`);
      setPost((p) => ({ ...p, votes: data.votes, voted: data.voted }));
    } catch (_) {}
  };

  const comment = async () => {
    if (!user) { toast("Sign in to comment"); return; }
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/community/posts/${id}/comments`, { body: text.trim() });
      setComments((c) => [...c, data]);
      setPost((p) => ({ ...p, comment_count: (p.comment_count || 0) + 1 }));
      setText("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't comment"); }
    finally { setBusy(false); }
  };

  const delPost = async () => {
    try { await api.delete(`/community/posts/${id}`); toast("Post deleted"); nav("/community"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't delete"); }
  };

  const delComment = async (cid) => {
    try {
      await api.delete(`/community/comments/${cid}`);
      setComments((c) => c.filter((x) => x.id !== cid));
      setPost((p) => ({ ...p, comment_count: Math.max((p.comment_count || 1) - 1, 0) }));
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't delete"); }
  };

  if (notFound) return <div className="grid h-[60vh] place-items-center text-white/40">Post not found.</div>;
  if (!post) return <div className="grid h-[60vh] place-items-center text-white/40">Loading…</div>;

  const canDeletePost = user && (user.role === "admin" || post.author_id === user.id);
  const tc = TOPIC_COLORS[post.topic] || "#00FFC2";

  return (
    <div className="mx-auto max-w-2xl px-5 py-8" data-testid="community-post-page">
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-2">
          <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider" style={{ background: `${tc}22`, color: tc }}>{post.topic}</span>
          <span className="text-xs text-white/40">{post.author_name} · {timeAgo(post.created_at)}</span>
          {canDeletePost && (
            <button onClick={delPost} data-testid="postdetail-delete"
              className="ml-auto flex items-center gap-1 text-xs text-white/40 transition-colors hover:text-[#FF0055]">
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>
        <h1 className="mt-3 font-display text-2xl font-black leading-tight tracking-tight">{post.title}</h1>
        <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-white/80">{post.body}</p>
        <div className="mt-4 flex items-center gap-4 border-t border-white/10 pt-4">
          <button onClick={vote} data-testid="postdetail-vote"
            className={`flex items-center gap-1.5 text-sm transition-colors ${post.voted ? "text-[#FF0055]" : "text-white/50 hover:text-white"}`}>
            <Heart size={16} className={post.voted ? "fill-[#FF0055]" : ""} /> {post.votes}
          </button>
          <span className="flex items-center gap-1.5 text-sm text-white/50"><MessageSquare size={16} /> {post.comment_count}</span>
        </div>
      </div>

      <h2 className="mb-3 mt-6 font-display text-lg font-bold tracking-tight">Comments</h2>

      {user ? (
        <div className="mb-5 flex items-start gap-2" data-testid="comment-box">
          <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={2000}
            placeholder="Add a comment…" data-testid="comment-input"
            className="min-h-[52px] flex-1 resize-y rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none focus:border-[#00FFC2]" />
          <Button onClick={comment} disabled={busy} data-testid="comment-submit"
            className="rounded-full bg-[#00FFC2] font-bold text-black hover:bg-[#00E0A8]"><Send size={16} /></Button>
        </div>
      ) : (
        <button onClick={() => nav("/auth")} className="mb-5 text-sm text-[#00FFC2]">Sign in to join the conversation →</button>
      )}

      <div className="space-y-3" data-testid="comment-list">
        {comments.length === 0 ? (
          <p className="text-sm text-white/40">No comments yet — start the conversation.</p>
        ) : comments.map((c) => {
          const canDel = user && (user.role === "admin" || c.author_id === user.id);
          return (
            <div key={c.id} className="rounded-xl bg-white/5 px-4 py-3" data-testid={`comment-${c.id}`}>
              <div className="flex items-center gap-2">
                <span className="grid h-6 w-6 place-items-center rounded-full bg-[#00FFC2] text-xs font-bold text-black">{c.author_name?.[0]}</span>
                <span className="text-xs font-semibold text-white/80">{c.author_name}</span>
                <span className="text-xs text-white/30">{timeAgo(c.created_at)}</span>
                {canDel && (
                  <button onClick={() => delComment(c.id)} data-testid={`comment-delete-${c.id}`}
                    className="ml-auto text-white/30 transition-colors hover:text-[#FF0055]"><Trash2 size={13} /></button>
                )}
              </div>
              <p className="mt-1.5 whitespace-pre-wrap text-sm text-white/70">{c.body}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
